/**
 * Instagram Feed Widget
 * Main widget class for embedding Instagram feeds
 * @version 1.0.0
 */

import { APIClient } from './utils/api-client.js';
import { Cache } from './utils/cache.js';
import { Lightbox } from './utils/lightbox.js';
import { GridLayout } from './layouts/grid.js';
import { CarouselLayout } from './layouts/carousel.js';
import { MasonryLayout } from './layouts/masonry.js';
import './styles/main.css';
import './styles/themes.css';
import './styles/lightbox.css';

/**
 * Instagram Widget Class
 */
export class InstagramWidget {
  /**
   * Creates an Instagram Widget instance
   * @param {string|HTMLElement} element - Container element or selector
   * @param {Object} config - Widget configuration
   */
  constructor(element, config = {}) {
    // Find container element
    this.container = typeof element === 'string'
      ? document.querySelector(element)
      : element;

    if (!this.container) {
      throw new Error(`Instagram Widget: Container element not found: ${element}`);
    }

    // Configuration
    this.config = {
      // API settings
      apiEndpoint: config.apiEndpoint || '/api/instagram-feed',
      limit: config.limit || 12,
      masonryExtraPostsForGapFilling: config.masonryExtraPostsForGapFilling !== false, // Fetch extra posts for masonry gap filling

      // Layout settings
      layout: config.layout || 'grid', // 'grid', 'carousel', 'masonry'
      columns: config.columns || 3,
      gap: config.gap || 16,
      aspectRatio: config.aspectRatio || '1/1',

      // Carousel specific
      itemWidth: config.itemWidth || 300,
      showArrows: config.showArrows !== false,
      autoScroll: config.autoScroll || false,
      autoScrollDelay: config.autoScrollDelay || 5000,

      // Theme settings
      theme: config.theme || 'light', // 'light', 'dark', 'custom'
      customColors: config.customColors || {},

      // Lightbox settings
      openInLightbox: config.openInLightbox !== false, // Enable lightbox by default
      lightboxShowViewOnInstagram: config.lightboxShowViewOnInstagram !== false,
      lightboxShowUsername: config.lightboxShowUsername !== false,
      lightboxShowCounter: config.lightboxShowCounter !== false,
      lightboxShowPostType: config.lightboxShowPostType !== false,
      lightboxShowTimestamp: config.lightboxShowTimestamp !== false,
      lightboxShowNavigation: config.lightboxShowNavigation !== false,
      lightboxShowCloseButton: config.lightboxShowCloseButton !== false,
      lightboxVideoControls: config.lightboxVideoControls !== false,
      lightboxVideoAutoplay: config.lightboxVideoAutoplay !== false,
      lightboxVideoLoop: config.lightboxVideoLoop !== false,
      lightboxVideoMuted: config.lightboxVideoMuted || false,

      // Filter settings
      filter: config.filter || null, // 'reels', 'feed', or null for all

      // Cache settings
      enableCache: config.enableCache !== false,
      cacheDuration: config.cacheDuration || 14400000, // 4 hours

      // Loading settings
      showLoading: config.showLoading !== false,
      loadingText: config.loadingText || 'Loading Instagram feed...',

      // Error settings
      showErrors: config.showErrors !== false,
      errorCallback: config.errorCallback || null,

      // Success callback
      onLoad: config.onLoad || null,

      // Debug mode
      debug: config.debug || false
    };

    // Initialize components
    this.apiClient = new APIClient(this.config.apiEndpoint.replace('/api/instagram-feed', ''));
    this.cache = this.config.enableCache
      ? new Cache('instagram_widget', this.config.cacheDuration)
      : null;

    // Layout instance
    this.layout = null;

    // Lightbox instance
    this.lightbox = null;

    // State
    this.posts = [];
    this.isLoading = false;
    this.error = null;

    // Apply theme
    this.applyTheme();

    // Log initialization in debug mode
    this.log('Instagram Widget initialized', this.config);
  }

  /**
   * Initializes and renders the widget
   * @returns {Promise<void>}
   */
  async init() {
    try {
      this.isLoading = true;
      this.showLoadingState();

      // Try to load from cache first
      if (this.cache) {
        const cacheKey = this.config.filter ? `feed_${this.config.filter}` : 'feed';
        const cachedPosts = this.cache.get(cacheKey);
        if (cachedPosts) {
          this.log('Loading from cache');
          this.posts = cachedPosts;
          this.render();
          this.isLoading = false;

          // Callback
          if (this.config.onLoad) {
            this.config.onLoad(this.posts);
          }

          return;
        }
      }

      // Fetch from API
      // For masonry layout, fetch extra posts for potential gap filling
      const fetchLimit = (this.config.layout === 'masonry' && this.config.masonryExtraPostsForGapFilling)
        ? this.config.limit + 8 // Fetch 8 extra posts for gap filling
        : this.config.limit;

      this.log('Fetching from API', { limit: fetchLimit });
      const response = await this.apiClient.fetchFeed(fetchLimit);

      if (!response.data || !Array.isArray(response.data)) {
        throw new Error('Invalid API response format');
      }

      this.posts = response.data;

      // Apply filter if specified
      if (this.config.filter) {
        this.posts = this.filterPosts(this.posts);
      }

      // Cache the posts with filter-specific key
      if (this.cache) {
        const cacheKey = this.config.filter ? `feed_${this.config.filter}` : 'feed';
        this.cache.set(cacheKey, this.posts);
      }

      // Render
      this.render();

      // Callback
      if (this.config.onLoad) {
        this.config.onLoad(this.posts);
      }

      this.log('Widget loaded successfully', {
        posts: this.posts.length,
        cached: response.cached || false
      });

    } catch (error) {
      this.error = error;
      this.log('Error loading widget', error, 'error');
      this.showErrorState(error);

      // Error callback
      if (this.config.errorCallback) {
        this.config.errorCallback(error);
      }
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Filters posts by media product type
   * @param {Array} posts - Posts to filter
   * @returns {Array} Filtered posts
   */
  filterPosts(posts) {
    if (!this.config.filter) {
      return posts;
    }

    const filterType = this.config.filter.toLowerCase();

    if (filterType === 'reels') {
      return posts.filter(post => post.media_product_type === 'REELS');
    } else if (filterType === 'feed') {
      return posts.filter(post => post.media_product_type === 'FEED');
    }

    return posts;
  }

  /**
   * Renders the widget
   */
  render() {
    if (!this.posts || this.posts.length === 0) {
      this.showEmptyState();
      return;
    }

    // Clear container
    this.container.innerHTML = '';

    // Create layout instance
    this.layout = this.createLayout();

    // Render layout
    const layoutElement = this.layout.render(this.posts, this.container);
    this.container.appendChild(layoutElement);

    // Add widget class for styling
    this.container.classList.add('ig-widget-container');
    this.container.setAttribute('data-layout', this.config.layout);
    this.container.setAttribute('data-theme', this.config.theme);
  }

  /**
   * Creates layout instance based on configuration
   * @returns {Object} Layout instance
   */
  createLayout() {
    const layoutConfig = {
      columns: this.config.columns,
      gap: this.config.gap,
      aspectRatio: this.config.aspectRatio,
      itemWidth: this.config.itemWidth,
      showArrows: this.config.showArrows,
      autoScroll: this.config.autoScroll,
      autoScrollDelay: this.config.autoScrollDelay,
      openInLightbox: this.config.openInLightbox,
      onItemClick: this.config.openInLightbox ? this.openLightbox.bind(this) : null,
      displayLimit: this.config.limit, // Initial posts to display
      enableGapFilling: this.config.masonryExtraPostsForGapFilling
    };

    switch (this.config.layout) {
      case 'carousel':
        return new CarouselLayout(layoutConfig);
      case 'masonry':
        return new MasonryLayout(layoutConfig);
      case 'grid':
      default:
        return new GridLayout(layoutConfig);
    }
  }

  /**
   * Opens lightbox at specific post index
   * @param {number} index - Post index
   */
  openLightbox(index) {
    if (!this.lightbox) {
      // Create lightbox with configuration
      const lightboxConfig = {
        showViewOnInstagram: this.config.lightboxShowViewOnInstagram,
        showUsername: this.config.lightboxShowUsername,
        showCounter: this.config.lightboxShowCounter,
        showPostType: this.config.lightboxShowPostType,
        showTimestamp: this.config.lightboxShowTimestamp,
        showNavigation: this.config.lightboxShowNavigation,
        showCloseButton: this.config.lightboxShowCloseButton,
        videoControls: this.config.lightboxVideoControls,
        videoAutoplay: this.config.lightboxVideoAutoplay,
        videoLoop: this.config.lightboxVideoLoop,
        videoMuted: this.config.lightboxVideoMuted
      };
      this.lightbox = new Lightbox(this.posts, lightboxConfig);
    }
    this.lightbox.open(index);
  }

  /**
   * Applies theme to widget
   */
  applyTheme() {
    const root = this.container;

    // Apply theme class
    root.classList.add(`ig-widget-theme-${this.config.theme}`);

    // Apply custom colors if provided
    if (this.config.theme === 'custom' && this.config.customColors) {
      const colors = this.config.customColors;

      if (colors.primary) root.style.setProperty('--ig-widget-primary', colors.primary);
      if (colors.secondary) root.style.setProperty('--ig-widget-secondary', colors.secondary);
      if (colors.background) root.style.setProperty('--ig-widget-bg', colors.background);
      if (colors.backgroundSecondary) root.style.setProperty('--ig-widget-bg-secondary', colors.backgroundSecondary);
      if (colors.text) root.style.setProperty('--ig-widget-text', colors.text);
      if (colors.textSecondary) root.style.setProperty('--ig-widget-text-secondary', colors.textSecondary);
    }
  }

  /**
   * Shows loading state
   */
  showLoadingState() {
    if (!this.config.showLoading) return;

    this.container.innerHTML = `
      <div class="ig-widget-loading">
        <div class="ig-widget-spinner"></div>
        <p>${this.escapeHtml(this.config.loadingText)}</p>
      </div>
    `;
  }

  /**
   * Shows error state
   * @param {Error} error - Error object
   */
  showErrorState(error) {
    if (!this.config.showErrors) {
      console.error('Instagram Widget Error:', error);
      return;
    }

    this.container.innerHTML = `
      <div class="ig-widget-error">
        <div class="ig-widget-error-icon">⚠️</div>
        <h3>Failed to load Instagram feed</h3>
        <p>${this.escapeHtml(error.message)}</p>
        <button class="ig-widget-retry-button">Retry</button>
      </div>
    `;

    // Add retry handler
    const retryButton = this.container.querySelector('.ig-widget-retry-button');
    if (retryButton) {
      retryButton.addEventListener('click', () => {
        this.refresh();
      });
    }
  }

  /**
   * Shows empty state
   */
  showEmptyState() {
    this.container.innerHTML = `
      <div class="ig-widget-empty">
        <div class="ig-widget-empty-icon">📷</div>
        <h3>No posts found</h3>
        <p>There are no Instagram posts to display.</p>
      </div>
    `;
  }

  /**
   * Refreshes the widget (clears cache and re-fetches)
   * @returns {Promise<void>}
   */
  async refresh() {
    if (this.cache) {
      this.cache.remove('feed');
    }
    await this.init();
  }

  /**
   * Updates widget configuration
   * @param {Object} newConfig - New configuration options
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    this.applyTheme();
    this.render();
  }

  /**
   * Destroys the widget and cleans up
   */
  destroy() {
    // Destroy layout if it has cleanup method
    if (this.layout && typeof this.layout.destroy === 'function') {
      this.layout.destroy();
    }

    // Destroy lightbox if it exists
    if (this.lightbox) {
      this.lightbox.destroy();
      this.lightbox = null;
    }

    // Clear container
    this.container.innerHTML = '';
    this.container.classList.remove('ig-widget-container');
    this.container.removeAttribute('data-layout');
    this.container.removeAttribute('data-theme');

    // Clear references
    this.posts = [];
    this.layout = null;
    this.error = null;
  }

  /**
   * Logs messages in debug mode
   * @param {string} message - Log message
   * @param {*} data - Additional data
   * @param {string} level - Log level (log, warn, error)
   */
  log(message, data = null, level = 'log') {
    if (!this.config.debug) return;

    const prefix = '[Instagram Widget]';
    if (data) {
      console[level](prefix, message, data);
    } else {
      console[level](prefix, message);
    }
  }

  /**
   * Escapes HTML to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Gets widget version
   * @returns {string} Version string
   */
  static get version() {
    return '1.0.0';
  }
}

// Export as default for UMD build
export default InstagramWidget;
